import { useCallback } from 'react';
import { useCurrencyStore } from '../../../stores/currency';

/**
 * The v2 quote page's modules take money and durations as already-formatted
 * strings — they render for the client and guest views too, and each of those
 * prices in its own currency (see `@codezo/quote-detail`'s `types.ts`).
 *
 * These are the admin side of that contract: one place that turns the API's
 * numbers into the strings the modules draw, so a figure can't be formatted two
 * ways on one page.
 */

export function useMoney() {
	const { selectedCurrency } = useCurrencyStore();
	return useCallback(
		(
			amount: number | null | undefined,
			currency?: string | null,
			/** A per-piece price: up to 4 decimals when the figure needs them. */
			opts?: { unit?: boolean }
		): string | null => {
			if (amount == null) return null;
			// Totals always at 2 decimals — never "DKK 12.5".
			return new Intl.NumberFormat('en-US', {
				style: 'currency',
				currency: currency || selectedCurrency,
				minimumFractionDigits: 2,
				maximumFractionDigits: opts?.unit ? 4 : 2,
			}).format(amount);
		},
		[selectedCurrency]
	);
}

/**
 * A money range for the overview strip — "DKK 40–52k".
 *
 * Rounded to thousands because the strip answers "roughly how big is this",
 * and a quote whose worth depends on which combination the client takes has no
 * exact figure to give until they answer.
 */
export function useCompactMoneyRange() {
	const { selectedCurrency } = useCurrencyStore();
	return useCallback(
		(low: number | null, high: number | null): string => {
			if (low == null && high == null) return '—';
			const round = (n: number) =>
				n >= 1000 ? `${Number((n / 1000).toFixed(1))}k` : `${Math.round(n)}`;
			const a = low != null ? round(low) : null;
			const b = high != null ? round(high) : null;
			const body = a && b ? (a === b ? a : `${a}–${b}`) : (a ?? b ?? '—');
			return `${selectedCurrency} ${body}`;
		},
		[selectedCurrency]
	);
}

/**
 * "18–24 days", collapsing to one figure when the bounds agree — and to "1 day"
 * when that figure is one.
 *
 * The singular is done by dropping a trailing "s", so it follows whatever unit
 * the caller passes and leaves abbreviations ("1 d") alone. It went unnoticed
 * while every caller abbreviated; the delivery table spelling the word out is
 * what turned it into "1 days" on screen.
 */
export function formatDayRange(
	min: number | null | undefined,
	max: number | null | undefined,
	unit = 'days'
): string | null {
	if (min == null && max == null) return null;
	const a = min ?? max!;
	const b = max ?? min!;
	if (a !== b) return `${a}–${b} ${unit}`;
	return `${a} ${a === 1 && unit.endsWith('s') ? unit.slice(0, -1) : unit}`;
}

/**
 * "18–24%", collapsing to one figure when the bounds agree.
 *
 * Precision follows the size of the number, because a margin can legitimately be
 * a fraction of a percent and whole-number rounding would print "0%" over a real
 * amount — stating the offer earns nothing when it earns a little. Below 10 it
 * keeps a decimal; below that a value too small to show at all becomes "<0.1%",
 * which is true, where 0% is not. An exact zero still reads "0%".
 */
/**
 * Percentages on this page, at ONE precision.
 *
 * Every percentage here is a figure someone reconciles — against the pricing
 * modal, or against e-conomic — so they are all quoted the same way, whether
 * they arrive alone or as the two ends of a range. The previous rule varied the
 * decimals with the magnitude (none at all from 10 upwards), which rendered a
 * margin of 16.67 as "17%" next to a modal reading "16,67%" for the identical
 * number, and made it look like the two screens disagreed.
 *
 * Two decimals, matching `panelPercent` in the pricing modal.
 */
const PERCENT_DECIMALS = 2;

/** Bare digits, no sign — shared so a range can never quote its ends differently. */
function percentDigits(value: number): string {
	// Below the precision we show, "0.00%" would read as nothing earned when
	// something was. Say it is small instead of saying it is zero.
	if (value !== 0 && Math.abs(value) < 0.01) return '<0.01';
	return value.toFixed(PERCENT_DECIMALS);
}

/** One percentage. */
export function formatPercent(value: number | null | undefined): string {
	if (value == null) return '—';
	return `${percentDigits(value)}%`;
}

/** A low–high percentage span, collapsing to one figure when the ends agree. */
export function formatPercentRange(
	low: number | null | undefined,
	high: number | null | undefined
): string {
	if (low == null && high == null) return '—';
	const lo = percentDigits(low ?? high!);
	const hi = percentDigits(high ?? low!);
	return lo === hi ? `${lo}%` : `${lo}–${hi}%`;
}
